import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { BrandSummary, Tag } from "@shared/types";
import { api } from "../lib/api";
import { BrandGrid } from "../components/BrandGrid";
import { TagPill } from "../components/TagPill";

export function LandingPage() {
  const [discover, setDiscover] = useState<BrandSummary[]>([]);
  const [fresh, setFresh] = useState<BrandSummary[]>([]);
  const [styles, setStyles] = useState<Tag[]>([]);
  const [categories, setCategories] = useState<Tag[]>([]);
  const [radarTrends, setRadarTrends] = useState<Awaited<ReturnType<typeof api.radar.trends>>["items"]>([]);

  useEffect(() => {
    api.discover().then((r) => setDiscover(r.items)).catch(() => setDiscover([]));
    api.brands.list({ fresh: "week", sort: "recent", limit: 8 })
      .then((r) => setFresh(r.items))
      .catch(() => setFresh([]));
    api.radar.trends(8)
      .then((r) => setRadarTrends(r.items))
      .catch(() => setRadarTrends([]));
    api.tags.list("style_direction", { occupied: true })
      .then((r) => setStyles([...r.tags].sort((a, b) => (b.brandCount ?? 0) - (a.brandCount ?? 0) || a.name.localeCompare(b.name)).slice(0, 16)))
      .catch(() => setStyles([]));
    api.categories.list({ occupied: true })
      .then((r) => setCategories(
        r.categories
          .filter((c) => c.type === "primary_category")
          .sort((a, b) => (b.brandCount ?? 0) - (a.brandCount ?? 0) || a.name.localeCompare(b.name))
      ))
      .catch(() => setCategories([]));
  }, []);

  return (
    <>
      <section className="hero">
        <div className="container">
          <div className="hero__eyebrow">Live brand radar</div>
          <h1 className="hero__title">Before<em>Mainstream</em></h1>
          <p className="hero__sub">
            Find brands before they become mainstream.
          </p>
          <div className="hero__cta">
            <Link to="/brands"><button className="primary">Browse the radar →</button></Link>
            <Link to="/submit"><button>Drop a brand</button></Link>
          </div>
        </div>
      </section>

      {fresh.length > 0 && (
        <section className="section section--fresh">
          <div className="container">
            <div className="section__head">
              <h2>New this week</h2>
              <Link to="/brands?fresh=week&sort=recent">Fresh drops →</Link>
            </div>
            <BrandGrid brands={fresh} />
          </div>
        </section>
      )}

      {radarTrends.length > 0 && (
        <section className="section section--signal-heat">
          <div className="container">
            <div className="section__head">
              <h2>Signal heat</h2>
              <Link to="/brands?fresh=week">Live radar →</Link>
            </div>
            <div className="radar-trends radar-trends--home" aria-label="Signals moving this week">
              <div className="radar-trends__head">
                <span>Moving this week</span>
                <b>Styles the radar keeps seeing</b>
              </div>
              <div className="radar-trends__rail">
                {radarTrends.map((trend) => (
                  <Link
                    key={`${trend.type}:${trend.slug}`}
                    to={trend.type === "primary_category" ? `/categories/${trend.slug}` : `/tags/${trend.slug}`}
                    className={trend.type === "primary_category" ? "radar-trend is-category" : "radar-trend"}
                  >
                    <span>{trend.name}</span>
                    <b>{trend.brandCount}</b>
                    <small>{trend.avgMomentum >= 70 ? "high momentum" : `disc ${trend.avgDiscovery}`}</small>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="section section--crowd">
        <div className="container">
          <div className="crowd-editorial">
            <div>
              <div className="hero__eyebrow">Crowd-powered radar</div>
              <h2>The crowd decides what is early.</h2>
              <p>
                Every brand can be voted Early, I'd wear, Too mainstream, or Not it.
                Those signals help surface the brands young people actually care about before they become obvious.
              </p>
            </div>
            <div className="crowd-editorial__steps" aria-label="Crowd voting options">
              <span>Early</span>
              <span>I'd wear</span>
              <span>Too mainstream</span>
              <span>Not it</span>
            </div>
            <Link to="/brands"><button className="primary">Vote on brands →</button></Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section__head">
            <h2>Heat right now 🔥</h2>
            <Link to="/brands">All brands →</Link>
          </div>
          <BrandGrid brands={discover} emptyMessage="Radar's warming up — first brands landing soon." />
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section__head"><h2>Vibes</h2></div>
          <div className="chip-row">
            {styles.map((s) => <TagPill key={s.id} tag={s} showCount />)}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section__head"><h2>Shop type</h2></div>
          <div className="chip-row">
            {categories.map((c) => <TagPill key={c.id} tag={c} showCount />)}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section__head"><h2>How the radar works</h2></div>
          <ol style={{ color: "var(--fg-muted)", lineHeight: 1.7, paddingLeft: "1.25rem", maxWidth: "60ch", fontSize: "0.95rem" }}>
            <li>We scan stockists, blogs and curated sources for new brand drops.</li>
            <li>An AI reads the site, pulls style + category + price signals.</li>
            <li>Each brand gets scored: discovery, mainstream, momentum.</li>
            <li>High-confidence ones publish on their own. The rest go to review.</li>
          </ol>
        </div>
      </section>
    </>
  );
}
